import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Divider,
  Link,
  Button,
  Grid2,
} from "@mui/material";

const MONTHS: Record<string, number> = {
  jan: 0,
  feb: 1,
  fev: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
  dez: 11,
};

function parseMonthYear(value: string): Date | null {
  const match = value.trim().match(/^([A-Za-z]+)\.?\s+(\d{4})$/);
  if (!match) {
    return null;
  }
  const month = MONTHS[match[1].toLowerCase()];
  if (month === undefined) {
    return null;
  }
  return new Date(Number(match[2]), month, 1);
}

function formatDuration(dates: string): string {
  const rangePart = dates.includes("/")
    ? dates.slice(dates.lastIndexOf("/") + 1).trim()
    : dates;
  const [startText, endText] = rangePart.split(/\s+-\s+/);
  const start = parseMonthYear(startText || "");
  if (!start || !endText) {
    return "";
  }

  const isCurrent = /^current$/i.test(endText.trim());
  const end = isCurrent ? new Date() : parseMonthYear(endText.trim());
  if (!end) {
    return "";
  }

  let months =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth());
  if (!isCurrent) {
    months += 1;
  }
  if (months < 1) {
    return "< 1 year";
  }

  const years = Math.floor(months / 12);
  const leftover = months % 12;
  const parts: string[] = [];
  if (years > 0) {
    parts.push(years === 1 ? "1 year" : `${years} years`);
  }
  if (leftover > 0) {
    parts.push(leftover === 1 ? "1 month" : `${leftover} months`);
  }
  return parts.join(" ");
}

interface ExperienceItemProps {
  title: string;
  subtitle?: string;
  link?: string;
  dates: string;
  description: string | React.ReactNode;
}

const ExperienceItem: React.FC<ExperienceItemProps> = ({
  title,
  subtitle,
  link,
  dates,
  description,
}) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h3" sx={{ fontWeight: "bold" }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary">
        {link ? (
          <Link href={link} target="_blank" rel="noopener noreferrer">
            {subtitle}
          </Link>
        ) : (
          subtitle
        )}
      </Typography>
    )}
    <Typography variant="body2" color="text.secondary">
      {dates}
    </Typography>
    <Box
      sx={{
        display: "inline-block",
        bgcolor: "primary.light",
        color: "primary.contrastText",
        px: 1,
        py: 0.5,
        borderRadius: 1,
        mt: 1,
      }}
    >
      {formatDuration(dates)}
    </Box>
    <Divider sx={{ my: 1 }} />
    <Typography variant="body1">{description}</Typography>
  </Box>
);

const allExperiences = [
  {
    title: "Fracsun (a Nextpower Company)",
    subtitle: "fracsun.com",
    link: "https://fracsun.com",
    dates: "Sep. 2021 - CURRENT",
    description: (
      <>
        SOFTWARE ENGINEER
        <br />• Staff Software engineer.
      </>
    ),
  },
  {
    title: "VetNebula",
    subtitle: "vetnebula.com",
    link: "https://www.vetnebula.com/",
    dates: "Jan. 2025 - CURRENT",
    description: "CTO",
  },
  {
    title: "FlexTogether",
    subtitle: "flextogether.com",
    link: "https://flextogether.com",
    dates: "Mar. 2022 - CURRENT",
    description: "CTO",
  },
  {
    title: "ShoCast",
    subtitle: "shocast.live",
    link: "https://shocast.live",
    dates: "Dez. 2020 - Jan. 2025",
    description: "Co-Founder",
  },
  {
    title: "Toptal",
    dates: "Campina Grande, PB, Brazil / Mar. 2017 - Feb. 2022",
    description: (
      <>
        SOFTWARE ENGINEER
        <br />• Mobile and Web software development with focus on Android,
        Python-Django and MEAN Stack.
      </>
    ),
  },
  {
    title: "Nokia Networks/UFCG",
    dates: "Campina Grande, PB, Brazil / Dez. 2017 - Jan. 2021",
    description: (
      <>
        SENIOR SOFTWARE DEVELOPER
        <br />• System involving face detection and recognition.
      </>
    ),
  },
  {
    title: "Sanmina/UFCG",
    dates: "Campina Grande, PB, Brazil / Apr. 2017 - Nov 2017",
    description: (
      <>
        TECH LEADER / SENIOR SOFTWARE DEVELOPER
        <br />• System to share live Medical Exams using WebRTC and MEAN Stack;
        <br />• Infrastructure for streaming transmissions over NAT.
      </>
    ),
  },
  {
    title: "Toshiba Medical/UFCG",
    dates: "Campina Grande, PB, Brazil / May. 2016 - Sep. 2017",
    description: (
      <>
        TECH LEADER / SENIOR SOFTWARE DEVELOPER
        <br />• Development of a streaming solution using MEAN stack + iOS,
        Android and Web clients.
      </>
    ),
  },
  {
    title: "Motorola Solutions/UFCG",
    dates: "Campina Grande, PB, Brazil / Fev. 2016 - May. 2016",
    description: (
      <>
        SOFTWARE DEVELOPER
        <br />• Mobile software development with focus on Android and Node.js.
      </>
    ),
  },
  {
    title: "Sony Mobile Communications/UFCG",
    dates: "Campina Grande, PB, Brazil / Apr. 2014 - Jan. 2016",
    description: (
      <>
        SOFTWARE DEVELOPER
        <br />• Mobile software development with focus on Android;
        <br />• Development of internal webhooks in Flask
      </>
    ),
  },
  {
    title: "E.life Monitor",
    dates: "São Paulo, SP, Brazil (remote) / Jan. 2011 - Jan. 2014",
    description: (
      <>
        SOFTWARE ENGINEER
        <br />• Project Management;
        <br />• Team Leadership;
        <br />• Mobile software development with focus on Android.
      </>
    ),
  },
  {
    title: "Nokia Institute of Technology/UFCG",
    dates: "Campina Grande, PB, Brazil / Mar. 2009 - Jan. 2010",
    description: (
      <>
        SOFTWARE DEVELOPER
        <br />• Development of a desktop software in PyQt to run on Unix (PyQt);
        <br />• Mobile software development with focus on Maemo Platform.
      </>
    ),
  },
];

const Experience: React.FC = () => {
  const [showAll, setShowAll] = useState(false);

  const displayedExperiences = showAll
    ? allExperiences
    : allExperiences.slice(0, 3);

  return (
    <Box sx={{ py: 8 }}>
      <Container>
        <Grid2 container spacing={4}>
          <Grid2 size={{ xs: 12 }}>
            <Typography variant="h2" gutterBottom sx={{ textAlign: "center" }}>
              Work Experience
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Typography
              variant="h5"
              sx={{ textAlign: { xs: "center", md: "left" } }}
            >
              Here are some of the things I've been working on.
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              {displayedExperiences.map((exp, index) => (
                <ExperienceItem key={index} {...exp} />
              ))}
              {allExperiences.length > 3 && (
                <Box sx={{ textAlign: "center", mt: 4 }}>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={() => setShowAll(!showAll)}
                  >
                    {showAll ? "Show Less" : "Show More"}
                  </Button>
                </Box>
              )}
            </Box>
          </Grid2>
        </Grid2>
      </Container>
    </Box>
  );
};

export default Experience;
